<!--
  AdminNotificationPreferences
  Admin-side UI for viewing and overriding a member's notification preferences.

  Props:
  userId  (String, required) — target user's _id
  userEmail (String) — target user's email (display only)

  Displays a 3×3 grid of channel × category toggles.
  Transactional toggles are greyed out and locked (immutable).
  Requires a reason when downgrading any active preference.
  Provides a "Global Unsubscribe" action with confirmation modal.
-->
<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axiosIns from '@/plugins/axios'

const props = defineProps({
  userId: { type: String, required: true },
  userEmail: { type: String, default: '' },
})

const loading = ref(false)
const saving = ref(false)
const snackbar = ref({ show: false, message: '', color: 'success' })

// Loaded from server
const currentPrefs = ref(null)
const deliveryHealth = ref(null)

// Edit state — copy of prefs that user can change
const editPrefs = ref(null)
const reason = ref('')

const unsubscribeDialog = ref(false)
const unsubscribeReason = ref('')
const unsubscribeLoading = ref(false)

const CHANNELS = ['email', 'inApp']
// RM-446: two categories — transactional (always on) and marketing.
const CATEGORIES = ['transactional', 'marketing']

const CHANNEL_LABELS = { email: 'Email', inApp: 'In-App', sms: 'SMS', slack: 'Slack' }
const CATEGORY_LABELS = {
  transactional: 'Transactional',
  marketing: 'Marketing',
}

const isGloballyUnsubscribed = computed(() => {
  return Boolean(currentPrefs.value?.unsubscribedAt)
})

function defaultPrefs() {
  const p = { unsubscribedAt: null }
  for (const ch of CHANNELS) {
    p[ch] = { transactional: true, marketing: false }
  }
  
  return p
}

async function loadPrefs() {
  if (!props.userId) return
  loading.value = true
  try {
    // Re-use the admin user endpoint to get prefs
    const res = await axiosIns.get(`/user/${props.userId}`)
    const user = res.data.user || res.data
    currentPrefs.value = user.notificationPreferences || defaultPrefs()
    editPrefs.value = JSON.parse(JSON.stringify(currentPrefs.value))
  } catch (_err) {
    currentPrefs.value = defaultPrefs()
    editPrefs.value = JSON.parse(JSON.stringify(currentPrefs.value))
  } finally {
    loading.value = false
  }
}

async function loadDeliveryHealth() {
  if (!props.userId) return
  try {
    const res = await axiosIns.get(`/admin/comms/users/${props.userId}/delivery-health`)
    if (res.data.success) {
      deliveryHealth.value = res.data.data
    }
  } catch (_err) {
    deliveryHealth.value = null
  }
}

// Check if saving would downgrade any preference
const hasDowngrade = computed(() => {
  if (!currentPrefs.value || !editPrefs.value) return false
  for (const ch of CHANNELS) {
    for (const cat of CATEGORIES) {
      if (cat === 'transactional') continue
      const was = (currentPrefs.value[ch] || {})[cat]
      const now = (editPrefs.value[ch] || {})[cat]
      if (was !== false && now === false) return true
    }
  }
  
  return false
})

function getPref(ch, cat) {
  return editPrefs.value?.[ch]?.[cat] ?? true
}

function setPref(ch, cat, val) {
  if (!editPrefs.value[ch]) editPrefs.value[ch] = {}
  editPrefs.value[ch][cat] = val
}

function isDirty() {
  if (!currentPrefs.value || !editPrefs.value) return false
  for (const ch of CHANNELS) {
    for (const cat of CATEGORIES) {
      const was = (currentPrefs.value[ch] || {})[cat]
      const now = (editPrefs.value[ch] || {})[cat]
      if (was !== now) return true
    }
  }
  
  return false
}

async function save() {
  if (!isDirty()) {
    showSnackbar('No changes to save', 'info')
    
    return
  }
  if (hasDowngrade.value && !reason.value?.trim()) {
    showSnackbar('A reason is required when downgrading a preference', 'error')
    
    return
  }

  // Build diff payload (only changed channels/categories)
  const preferences = {}
  for (const ch of CHANNELS) {
    for (const cat of CATEGORIES) {
      if (cat === 'transactional') continue
      const was = (currentPrefs.value[ch] || {})[cat]
      const now = (editPrefs.value[ch] || {})[cat]
      if (was !== now) {
        if (!preferences[ch]) preferences[ch] = {}
        preferences[ch][cat] = now
      }
    }
  }

  saving.value = true
  try {
    const res = await axiosIns.put(`/admin/comms/users/${props.userId}/preferences`, {
      preferences,
      reason: reason.value || null,
    })
    if (res.data.success) {
      currentPrefs.value = res.data.data.notificationPreferences
      editPrefs.value = JSON.parse(JSON.stringify(currentPrefs.value))
      reason.value = ''
      showSnackbar('Preferences updated', 'success')
    }
  } catch (err) {
    showSnackbar(err.response?.data?.error || 'Save failed', 'error')
  } finally {
    saving.value = false
  }
}

function cancelEdit() {
  editPrefs.value = JSON.parse(JSON.stringify(currentPrefs.value))
  reason.value = ''
}

async function applyGlobalUnsubscribe() {
  if (!unsubscribeReason.value?.trim()) {
    showSnackbar('Reason is required for global unsubscribe', 'error')
    
    return
  }
  unsubscribeLoading.value = true
  try {
    const res = await axiosIns.put(`/admin/comms/users/${props.userId}/preferences`, {
      applyUnsubscribeGlobally: true,
      reason: unsubscribeReason.value,
    })
    if (res.data.success) {
      currentPrefs.value = res.data.data.notificationPreferences
      editPrefs.value = JSON.parse(JSON.stringify(currentPrefs.value))
      unsubscribeDialog.value = false
      unsubscribeReason.value = ''
      showSnackbar('Global unsubscribe applied', 'warning')
    }
  } catch (err) {
    showSnackbar(err.response?.data?.error || 'Unsubscribe failed', 'error')
  } finally {
    unsubscribeLoading.value = false
  }
}

function showSnackbar(message, color = 'success') {
  snackbar.value = { show: true, message, color }
}

watch(() => props.userId, () => {
  loadPrefs()
  loadDeliveryHealth()
})

onMounted(() => {
  loadPrefs()
  loadDeliveryHealth()
})
</script>

<template>
  <div class="admin-notif-prefs">
    <div class="d-flex align-center mb-3">
      <span class="text-subtitle-1 font-weight-medium">Notification Preferences</span>
      <VSpacer />
      <VBtn
        size="small"
        color="error"
        variant="outlined"
        @click="unsubscribeDialog = true"
      >
        Global Unsubscribe
      </VBtn>
    </div>

    <!-- Delivery health banner -->
    <VAlert
      v-if="deliveryHealth && (deliveryHealth.hardBounces > 0 || deliveryHealth.complaints > 0 || deliveryHealth.activeSuppressions > 0)"
      type="warning"
      density="compact"
      variant="tonal"
      class="mb-3"
    >
      Delivery health (last 30d):
      <strong>{{ deliveryHealth.hardBounces }}</strong> hard bounce(s),
      <strong>{{ deliveryHealth.complaints }}</strong> complaint(s),
      <strong>{{ deliveryHealth.activeSuppressions }}</strong> active suppression(s).
      <VBtn
        size="x-small"
        variant="text"
        to="/admin-comms-suppressions"
        class="ml-1"
      >
        View suppressions
      </VBtn>
    </VAlert>

    <VAlert
      v-if="isGloballyUnsubscribed"
      type="error"
      density="compact"
      variant="tonal"
      class="mb-3"
    >
      This member is globally unsubscribed. Only transactional emails are delivered.
    </VAlert>

    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-3"
    />

    <div v-if="editPrefs">
      <!-- Preference grid -->
      <VTable
        density="compact"
        class="mb-3"
      >
        <thead>
          <tr>
            <th>Channel</th>
            <th
              v-for="cat in CATEGORIES"
              :key="cat"
              class="text-center"
            >
              {{ CATEGORY_LABELS[cat] }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="ch in CHANNELS"
            :key="ch"
          >
            <td>{{ CHANNEL_LABELS[ch] }}</td>
            <td
              v-for="cat in CATEGORIES"
              :key="cat"
              class="text-center"
            >
              <VSwitch
                :model-value="getPref(ch, cat)"
                :disabled="cat === 'transactional'"
                :title="cat === 'transactional' ? 'Transactional preferences are immutable' : undefined"
                density="compact"
                hide-details
                class="d-inline-flex"
                @update:model-value="(v) => setPref(ch, cat, v)"
              />
            </td>
          </tr>
        </tbody>
      </VTable>

      <!-- Reason field — required when downgrading -->
      <VTextField
        v-if="isDirty()"
        v-model="reason"
        label="Reason for change"
        :hint="hasDowngrade ? 'Required — you are downgrading at least one preference' : 'Optional'"
        :persistent-hint="hasDowngrade"
        :required="hasDowngrade"
        density="compact"
        class="mb-3"
      />

      <div class="d-flex gap-2">
        <VBtn
          color="primary"
          :loading="saving"
          :disabled="!isDirty()"
          @click="save"
        >
          Save
        </VBtn>
        <VBtn
          variant="outlined"
          :disabled="!isDirty()"
          @click="cancelEdit"
        >
          Cancel
        </VBtn>
      </div>
    </div>

    <!-- Global Unsubscribe confirmation -->
    <VDialog
      v-model="unsubscribeDialog"
      max-width="450"
    >
      <VCard>
        <VCardTitle>Apply Global Unsubscribe</VCardTitle>
        <VCardText>
          <VAlert
            type="warning"
            density="compact"
            class="mb-3"
          >
            This sets <code>unsubscribedAt</code> to now. The member will only receive
            transactional emails (password reset, booking confirmations, etc.) until
            the preference is cleared.
          </VAlert>
          <VTextField
            v-model="unsubscribeReason"
            label="Reason (required)"
            placeholder="e.g. Member called and requested full opt-out"
            required
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn @click="unsubscribeDialog = false">
            Cancel
          </VBtn>
          <VBtn
            color="error"
            :loading="unsubscribeLoading"
            :disabled="!unsubscribeReason.trim()"
            @click="applyGlobalUnsubscribe"
          >
            Apply Unsubscribe
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VSnackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="3000"
    >
      {{ snackbar.message }}
    </VSnackbar>
  </div>
</template>

<style scoped>
.admin-notif-prefs {
  padding: 4px 0;
}
.gap-2 {
  gap: 8px;
}
</style>
